import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router";
import PageMeta from "../components/common/PageMeta";
import PageContainer from "../components/common/PageContainer";
import { useApi } from "../hooks/useApi";
import { useStateValue } from "../context/StateValueContext";
import { useCompanyId } from "../hooks/useCompanyId";
import {
  Box,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddAccountButton from "../components/common/AddAccountButton";
import PaymentProcessorsTable from "../components/common/PaymentProcessorsTable";
import BrandsManager from "../components/common/BrandsManager";
import OrderInsightMerchantInfoManager from "../components/common/OrderInsightMerchantInfoManager";
import CompanyUsersManager from "../components/common/CompanyUsersManager";
import CompanyBillingManager from "../components/common/CompanyBillingManager";
import CompanySettingsManager from "../components/common/CompanySettingsManager";
import ApiKeysManager from "../components/common/ApiKeysManager";
import StorefrontsPanel from "../components/company/StorefrontsPanel";
import GoogleSheetMidsManager from "../components/company/GoogleSheetMidsManager";
import OnboardingButton from "./Onboarding/OnboardingButton";
import { hasPermission } from "../functions/permissionUtils";
import { PERMISSIONS } from "../constants/permissions";
import StripeDialog from "../components/common/StripeDialog";
import PayPalDialog from "../components/common/PayPalDialog";
import ShopifyLegacyDialog from "../components/common/ShopifyLegacyDialog";
import CheckoutChampDialog from "../components/common/CheckoutChampDialog";
import CrmConnectorDialog from "../components/common/CrmConnectorDialog";
import AdyenDialog from "../components/common/AdyenDialog";
import OtherAccountDialog from "../components/common/OtherAccountDialog";
import AdyenMerchantBrandsDialog from "../components/common/AdyenMerchantBrandsDialog";
import { PPAccountIntegration, Brand, PpAccountSettingsPayload, CredentialProbeResult } from "../types/paymentProcessor";
import { DownloadDisputesResponse } from "../types/downloadDisputesResponse";
import { AdyenDisputeImportResult } from "../types/adyenDisputeImport";
import { formatAdyenImportResult } from "../utils/formatAdyenImportResult";
import { AdyenPaymentImportResult } from "../types/adyenPaymentImport";
import { formatAdyenPaymentImportResult } from "../utils/formatAdyenPaymentImportResult";
import { AdyenPaymentDayCount, AdyenPaymentDaysResponse } from "../types/adyenPaymentDays";
import { CompanySettings } from "../types/companySettings";
import EditIcon from '@mui/icons-material/Edit';

type SettingsTab = 'users' | 'accounts' | 'brands' | 'orderInsight' | 'evidence' | 'alerts' | 'billing' | 'settings' | 'apiKeys';
const DEFAULT_TAB: SettingsTab = 'users';

export default function AdminCompanyDetails() {
  const { companyId: selectedCompanyId } = useCompanyId();
  const companyId = selectedCompanyId ? String(selectedCompanyId) : undefined;
  const [{ miniCompanies, user }, dispatch] = useStateValue();
  const { get, post, postForm, patch, loading, error } = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ppAccounts, setPpAccounts] = useState<PPAccountIntegration[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);

  const isChargemont = companyId === '1';
  const canSeeBilling = hasPermission(user, PERMISSIONS.BILLING_ALL);
  const canSeeApiKeys = hasPermission(user, PERMISSIONS.API_KEYS_ALL);
  const availableTabs: SettingsTab[] = isChargemont
    ? ['users']
    : (['users', 'accounts', 'brands', 'orderInsight', 'evidence', 'alerts', ...(canSeeBilling ? ['billing' as const] : []), 'settings', ...(canSeeApiKeys ? ['apiKeys' as const] : [])]);

  const tabFromUrl = searchParams.get('tab') as SettingsTab | null;
  const initialTab: SettingsTab = tabFromUrl && availableTabs.includes(tabFromUrl) ? tabFromUrl : DEFAULT_TAB;
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);

  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(DEFAULT_TAB);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, canSeeBilling]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (activeTab !== DEFAULT_TAB) {
      params.set('tab', activeTab);
    } else {
      params.delete('tab');
    }
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const [brandCreateRequested, setBrandCreateRequested] = useState(false);
  const [downloadingAccountId, setDownloadingAccountId] = useState<number | null>(null);
  const [openStripeDialog, setOpenStripeDialog] = useState(false);
  const [openPayPalDialog, setOpenPayPalDialog] = useState(false);
  const [openShopifyLegacyDialog, setOpenShopifyLegacyDialog] = useState(false);
  const [openCheckoutChampDialog, setOpenCheckoutChampDialog] = useState(false);
  const [openCrmConnectorDialog, setOpenCrmConnectorDialog] = useState(false);
  const [openAdyenDialog, setOpenAdyenDialog] = useState(false);
  const [openOtherDialog, setOpenOtherDialog] = useState(false);
  const [mappingAccount, setMappingAccount] = useState<{ accountId: number; name: string } | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const fetchPpAccounts = useCallback(async () => {
    if (!companyId) return;

    try {
      const response = await get<PPAccountIntegration[]>(`/api/companies/${companyId}/pp-accounts`);
      setPpAccounts(response);
    } catch (err) {
      console.error('Failed to fetch payment processors:', err);
    }
  }, [get, companyId]);

  const fetchBrands = useCallback(async () => {
    if (!companyId) return;

    try {
      const response = await get<Brand[]>(`/api/companies/${companyId}/brands`);
      setBrands(response);
    } catch (err) {
      console.error('Failed to fetch brands:', err);
    }
  }, [get, companyId]);

  const fetchCompanySettings = useCallback(async () => {
    if (!companyId) return;

    try {
      const response = await get<CompanySettings>(`/api/companies/${companyId}/settings`);
      setCompanySettings(response);
    } catch (err) {
      console.error('Failed to fetch company settings:', err);
    }
  }, [get, companyId]);

  const refreshBrands = useCallback(() => {
    fetchBrands();
  }, [fetchBrands]);

  useEffect(() => {
    fetchPpAccounts();
    fetchBrands();
    fetchCompanySettings();
  }, [fetchPpAccounts, fetchBrands, fetchCompanySettings]);

  const handleStripeDownloadDisputes = async (accountId: number) => {
    setDownloadingAccountId(accountId);
    try {
      const response = await post<DownloadDisputesResponse>(`/api/companies/${companyId}/pp-accounts/${accountId}/download-stripe-disputes`, {}, { slowApi: true });
      setSnackbar({
        open: true,
        message: `Download completed (${response.newDisputesCount} new disputes, ${response.updatedDisputesCount ?? 0} updated disputes)`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to download disputes',
        severity: 'error',
      });
    } finally {
      setDownloadingAccountId(null);
    }
  };

  const handleAdyenImportDisputes = async (accountId: number, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await postForm<AdyenDisputeImportResult>(
        `/api/companies/${companyId}/pp-accounts/${accountId}/import-adyen-disputes`,
        formData,
        { slowApi: true },
      );
      setSnackbar({ open: true, message: formatAdyenImportResult(result), severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to import dispute report', severity: 'error' });
      throw err;
    }
  };

  const handleAdyenPaymentDays = async (accountId: number): Promise<AdyenPaymentDayCount[]> => {
    const result = await get<AdyenPaymentDaysResponse>(
      `/api/companies/${companyId}/pp-accounts/${accountId}/adyen-payment-days`,
    );
    return result.days;
  };

  const handleVerifyCheckoutChampCredentials = async (accountId: number): Promise<CredentialProbeResult[]> => {
    const data = await post<{ results: CredentialProbeResult[] }>(
      `/api/companies/${companyId}/pp-accounts/checkout-champ/${accountId}/verify-credentials`,
      {},
      { slowApi: true },
    );
    return data.results;
  };

  const handleChangeCheckoutChampPassword = async (accountId: number, password: string) => {
    await patch(
      `/api/companies/${companyId}/pp-accounts/checkout-champ/${accountId}/admin`,
      { password },
      { slowApi: true },
    );
    setSnackbar({ open: true, message: 'Password updated', severity: 'success' });
  };

  const handleAdyenImportPayments = async (accountId: number, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await postForm<AdyenPaymentImportResult>(
        `/api/companies/${companyId}/pp-accounts/${accountId}/import-adyen-payments`,
        formData,
        { slowApi: true },
      );
      setSnackbar({ open: true, message: formatAdyenPaymentImportResult(result), severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.message || 'Failed to import transaction report', severity: 'error' });
      throw err;
    }
  };

  const handleShopifyDownloadDisputes = async (accountId: number) => {
    setDownloadingAccountId(accountId);
    try {
      const response = await post<DownloadDisputesResponse>(`/api/companies/${companyId}/pp-accounts/${accountId}/download-shopify-disputes`, {}, { slowApi: true });
      setSnackbar({
        open: true,
        message: `Download completed (${response.newDisputesCount} new disputes, ${response.updatedDisputesCount ?? 0} updated disputes)`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to download disputes',
        severity: 'error',
      });
    } finally {
      setDownloadingAccountId(null);
    }
  };

  const handlePayPalDownloadDisputes = async (accountId: number) => {
    setDownloadingAccountId(accountId);
    try {
      const response = await post<DownloadDisputesResponse>(`/api/companies/${companyId}/pp-accounts/${accountId}/download-paypal-disputes`, {}, { slowApi: true });
      setSnackbar({
        open: true,
        message: `Download completed (${response.newDisputesCount} new disputes, ${response.updatedDisputesCount ?? 0} updated disputes)`,
        severity: 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to download disputes',
        severity: 'error',
      });
    } finally {
      setDownloadingAccountId(null);
    }
  };

  const handleAccountSettingsSave = async (
    ppType: string,
    accountId: number,
    payload: PpAccountSettingsPayload,
  ) => {
    await patch(`/api/companies/${companyId}/pp-accounts/${ppType.replace(/_/g, '-')}/${accountId}/admin`, payload, { slowApi: ppType === 'crm_connector' });
    await fetchPpAccounts();
    setSnackbar({ open: true, message: 'Account settings updated', severity: 'success' });
  };

  const handleAccountRefundMaxSave = async (ppType: string, accountId: number, cents: number | null) => {
    await patch(`/api/companies/${companyId}/pp-accounts/${ppType}/${accountId}`, { refundMaxAmount: cents });
    await fetchPpAccounts();
    setSnackbar({ open: true, message: 'Account max refund amount updated', severity: 'success' });
  };

  const handleAccountRepresentmentStartDateSave = async (ppType: string, accountId: number, date: string) => {
    await patch(`/api/companies/${companyId}/pp-accounts/${ppType}/${accountId}`, { representmentStartDate: date });
    await fetchPpAccounts();
    setSnackbar({ open: true, message: 'Representment start date updated', severity: 'success' });
  };

  const companyName = companyId ? miniCompanies.find(c => c.id === Number(companyId))?.name : undefined;
  const companyRefundMaxAmount = companySettings?.refund_max_amount;

  const handleStartEditName = () => {
    setEditName(companyName || '');
    setIsEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 0);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditName('');
  };

  const handleSaveName = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === companyName) {
      handleCancelEditName();
      return;
    }
    try {
      await patch(`/api/companies/${companyId}`, { name: trimmed });
      dispatch({
        type: 'SET_MINI_COMPANIES',
        miniCompanies: miniCompanies.map(c =>
          c.id === Number(companyId) ? { ...c, name: trimmed } : c
        ),
      });
      setIsEditingName(false);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.message || 'Failed to update company name',
        severity: 'error',
      });
    }
  };

  const handleBrandChange = async (ppType: string, accountId: number, brandId: number) => {
    try {
      await patch(`/api/companies/${companyId}/pp-accounts/${ppType}/${accountId}`, { brandId });
      await fetchPpAccounts();
    } catch {
      // error is set by useApi
    }
  };

  if (!companyId) {
    return (
      <div>
        <PageMeta title="Chargemont | Company Settings" description="Chargemont | Company Settings" />
        <PageContainer>
          <h3 className="font-semibold text-gray-800 text-theme-xl dark:text-white/90 sm:text-2xl mb-4">
            Company Settings
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Select a company from the dropdown above to view settings.
          </p>
        </PageContainer>
      </div>
    );
  }

  const tabLabels: Record<SettingsTab, string> = {
    users: 'Users',
    accounts: 'Payment Processors',
    brands: 'Brands',
    orderInsight: 'Order Insight',
    evidence: 'Evidence',
    alerts: 'Alerts',
    billing: 'Billing',
    settings: 'Settings',
    apiKeys: 'API Keys',
  };

  return (
    <div>
      <PageMeta
        title="Chargemont | Company Settings"
        description="Chargemont | Company Settings"
      />
      <PageContainer>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          {isEditingName ? (
            <input
              ref={nameInputRef}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') handleCancelEditName();
              }}
              onBlur={handleSaveName}
              className="font-semibold text-gray-800 text-theme-xl dark:text-white/90 sm:text-2xl bg-transparent border-b-2 border-brand-500 outline-none w-full"
            />
          ) : (
            (isChargemont ? (
              <h3 className="font-semibold text-gray-800 text-theme-xl dark:text-white/90 sm:text-2xl">
                Chargemont
              </h3>
            ) : (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                }}
                onClick={handleStartEditName}
              >
                <h3 className="font-semibold text-gray-800 text-theme-xl dark:text-white/90 sm:text-2xl">
                  {companyName || 'Company Details'}
                </h3>
                <EditIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </Box>
            ))
          )}
          {selectedCompanyId && !isChargemont && <OnboardingButton companyId={selectedCompanyId} />}
        </div>

        {availableTabs.length > 1 && (
          <div className="mb-4">
            <ToggleButtonGroup
              value={activeTab}
              exclusive
              onChange={(_, newTab: SettingsTab | null) => {
                if (newTab !== null) setActiveTab(newTab);
              }}
              size="small"
              sx={{
                height: 40,
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  px: 2.5,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  borderColor: 'divider',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              {availableTabs.map(tab => (
                <ToggleButton key={tab} value={tab}>{tabLabels[tab]}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>
        )}

        {activeTab === 'users' && (
          <CompanyUsersManager companyId={companyId} />
        )}

        {activeTab === 'accounts' && !isChargemont && (
          <div className="mb-8">
            <div className="mb-4 flex items-center justify-end gap-2">
              <AddAccountButton label="+ Stripe" onClick={() => setOpenStripeDialog(true)} />
              <AddAccountButton label="+ Shopify Payments (custom app)" onClick={() => setOpenShopifyLegacyDialog(true)} />
              <AddAccountButton label="+ PayPal" onClick={() => setOpenPayPalDialog(true)} />
              <AddAccountButton label="+ Checkout Champ" onClick={() => setOpenCheckoutChampDialog(true)} />
              <AddAccountButton label="+ Adyen" onClick={() => setOpenAdyenDialog(true)} />
              <AddAccountButton label="+ CRM Connector" onClick={() => setOpenCrmConnectorDialog(true)} />
              <AddAccountButton label="+ Other processor" onClick={() => setOpenOtherDialog(true)} />
            </div>
            {error && (
              <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                Error: {error}
              </Box>
            )}
            <PaymentProcessorsTable
              accounts={ppAccounts}
              loading={loading}
              downloadingAccountId={downloadingAccountId}
              onAdyenImportDisputes={handleAdyenImportDisputes}
              onAdyenImportPayments={handleAdyenImportPayments}
              onAdyenPaymentDays={handleAdyenPaymentDays}
              onVerifyCheckoutChampCredentials={handleVerifyCheckoutChampCredentials}
              onChangeCheckoutChampPassword={handleChangeCheckoutChampPassword}
              handleStripeDownloadDisputes={handleStripeDownloadDisputes}
              handleShopifyDownloadDisputes={handleShopifyDownloadDisputes}
              handlePayPalDownloadDisputes={handlePayPalDownloadDisputes}
              brands={brands}
              companyRefundMaxAmount={companyRefundMaxAmount}
              onBrandChange={handleBrandChange}
              onAdyenMapMerchants={(accountId, name) => setMappingAccount({ accountId, name })}
              onAccountSettingsSave={handleAccountSettingsSave}
              onRefundMaxSave={handleAccountRefundMaxSave}
              onRepresentmentStartDateSave={handleAccountRepresentmentStartDateSave}
            />
          </div>
        )}

        {activeTab === 'brands' && !isChargemont && (
          <div>
            <div className="mb-4 flex items-center justify-end gap-2">
              <AddAccountButton label="+ Brand" onClick={() => setBrandCreateRequested(true)} />
            </div>
            <BrandsManager
              brands={brands}
              onBrandsChange={() => { refreshBrands(); fetchPpAccounts(); }}
              createRequested={brandCreateRequested}
              onCreateHandled={() => setBrandCreateRequested(false)}
              brandsApiUrl={`/api/companies/${companyId}/brands`}
              billingProfilesApiUrl={`/api/companies/${companyId}/billing-profiles`}
            />
          </div>
        )}

        {activeTab === 'orderInsight' && !isChargemont && (
          <OrderInsightMerchantInfoManager companyId={companyId} />
        )}

        {activeTab === 'evidence' && !isChargemont && selectedCompanyId && (
          <StorefrontsPanel
            companyId={selectedCompanyId}
            openStorefrontId={Number(searchParams.get('storefront')) || null}
            onSaved={message => setSnackbar({ open: true, message, severity: 'success' })}
          />
        )}

        {activeTab === 'alerts' && !isChargemont && (
          <GoogleSheetMidsManager companyId={companyId} />
        )}

        {activeTab === 'billing' && !isChargemont && canSeeBilling && (
          <CompanyBillingManager companyId={companyId} />
        )}

        {activeTab === 'settings' && !isChargemont && (
          <CompanySettingsManager companyId={companyId} companyName={companyName} />
        )}

        {activeTab === 'apiKeys' && !isChargemont && (
          <ApiKeysManager apiBaseUrl={`/api/companies/${companyId}/api-keys`} />
        )}

        <StripeDialog
          open={openStripeDialog}
          onClose={() => setOpenStripeDialog(false)}
          onSuccess={fetchPpAccounts}
          apiUrl={`/api/companies/${companyId}/pp-accounts/stripe`}
          brands={brands}
          refreshBrands={refreshBrands}
          createBrandUrl={`/api/companies/${companyId}/brands`}
          showRepresentmentStartDate
        />

        <PayPalDialog
          open={openPayPalDialog}
          onClose={() => setOpenPayPalDialog(false)}
          onSuccess={fetchPpAccounts}
          apiUrl={`/api/companies/${companyId}/pp-accounts/paypal`}
          brands={brands}
          refreshBrands={refreshBrands}
          createBrandUrl={`/api/companies/${companyId}/brands`}
          showRepresentmentStartDate
        />

        <ShopifyLegacyDialog
          open={openShopifyLegacyDialog}
          onClose={() => setOpenShopifyLegacyDialog(false)}
          onSuccess={fetchPpAccounts}
          apiUrl={`/api/companies/${companyId}/pp-accounts/shopify`}
          brands={brands}
          refreshBrands={refreshBrands}
          createBrandUrl={`/api/companies/${companyId}/brands`}
          showRepresentmentStartDate
        />

        <CheckoutChampDialog
          open={openCheckoutChampDialog}
          onClose={() => setOpenCheckoutChampDialog(false)}
          onSuccess={fetchPpAccounts}
          apiUrl={`/api/companies/${companyId}/pp-accounts/checkout-champ`}
        />

        <CrmConnectorDialog
          open={openCrmConnectorDialog}
          onClose={() => setOpenCrmConnectorDialog(false)}
          onSuccess={fetchPpAccounts}
          apiUrl={`/api/companies/${companyId}/pp-accounts/crm-connector`}
        />

        <AdyenDialog
          open={openAdyenDialog}
          onClose={() => setOpenAdyenDialog(false)}
          onSuccess={async (created) => {
            await fetchPpAccounts();
            if (created.mappingRequired) {
              setMappingAccount({ accountId: created.adyenAccountId, name: created.name });
            }
          }}
          apiUrl={`/api/companies/${companyId}/pp-accounts/adyen`}
          environments={['live', 'test']}
          showRepresentmentStartDate
        />

        <OtherAccountDialog
          open={openOtherDialog}
          onClose={() => setOpenOtherDialog(false)}
          onSuccess={fetchPpAccounts}
          apiUrl={`/api/companies/${companyId}/pp-accounts/other`}
          brands={brands}
          refreshBrands={refreshBrands}
          createBrandUrl={`/api/companies/${companyId}/brands`}
        />

        <AdyenMerchantBrandsDialog
          open={mappingAccount !== null}
          accountId={mappingAccount?.accountId ?? null}
          accountName={mappingAccount?.name}
          apiBase={`/api/companies/${companyId}/pp-accounts/adyen`}
          brands={brands}
          refreshBrands={refreshBrands}
          createBrandUrl={`/api/companies/${companyId}/brands`}
          onClose={() => setMappingAccount(null)}
          onSaved={fetchPpAccounts}
        />

        <Snackbar
          open={snackbar.open}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

      </PageContainer>
    </div>
  );
}
